import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklistAnalytics } from '@portal-core/project-checklists/models/project-checklist-analytics.model';
import { ProjectChecklistItemsGridPathFilter } from '@portal-core/project-checklists/models/project-checklist-items-grid-path-filter.model';
import { ProjectChecklistItem } from '@portal-core/project-checklists/models/project-checklist-item.model';
import { ProjectChecklistUpdateItemNote } from '@portal-core/project-checklists/models/project-checklist-update-item-note.model';
import { ProjectChecklistUpdateItemStatuses } from '@portal-core/project-checklists/models/project-checklist-update-item-statuses.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable, tap } from 'rxjs';

@Service()
export class ProjectChecklistsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getChecklistById$(checklistId: number): Observable<ProjectChecklist> {
    return this.http.get<ProjectChecklist>(`${this.apiService.centralApiBaseUri}/api/checklists/${checklistId}`);
  }

  @SharePendingRequest()
  getChecklistsPage$(projectId: number, filter?: PageFilter): Observable<Page<ProjectChecklist>> {
    return this.http.post<Page<ProjectChecklist>>(`${this.apiService.centralApiBaseUri}/api/checklists/project/${projectId}`, filter);
  }

  @SharePendingRequest()
  getChecklistsNameAndIdByProjectId$(projectId: number): Observable<ProjectChecklist[]> {
    return this.http.get<ProjectChecklist[]>(`${this.apiService.centralApiBaseUri}/api/checklists/project/${projectId}/nameAndId`);
  }

  @SharePendingRequest()
  getChecklistItemsGridById$(projectChecklistId: number, checklistType: ProjectChecklistType, filtersWithPath: ProjectChecklistItemsGridPathFilter): Observable<Page<ProjectChecklistItem>> {
    return this.http.post<Page<ProjectChecklistItem>>(
      `${this.apiService.centralApiBaseUri}/api/checklists/${projectChecklistId}/itemsGrid`,
      filtersWithPath
    ).pipe(tap(gridPage => {
      gridPage.Items.map(item => {
        // set because we are using virtual nodes.
        if (checklistType === ProjectChecklistType.ProjectFiles) {
          item.Id = projectChecklistId + item.Path;
          item.ChecklistId = projectChecklistId;
        }
        return item;
      });
    }));
  }

  @SharePendingRequest()
  getChecklistAnalyticsByIds$(checklistIds: number[]): Observable<Dictionary<ProjectChecklistAnalytics>> {
    return this.http.post<Dictionary<ProjectChecklistAnalytics>>(`${this.apiService.centralApiBaseUri}/api/checklists/analytics`, checklistIds);
  }

  // Validity is signalled by the status code - 200 for available, 400 for taken - not by a body.
  @SharePendingRequest()
  verifyChecklistName$(projectId: number, checklistName: string): Observable<void> {
    return this.http.get<void>(`${this.apiService.centralApiBaseUri}/api/checklists/project/${projectId}/verifyChecklistName?checklistName=${encodeURIComponent(checklistName)}`);
  }

  updateChecklistItemStatus$(checklistItemsToUpdate: ProjectChecklistUpdateItemStatuses[]): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/checklists/itemStatuses`, checklistItemsToUpdate);
  }

  updateItemNote$(item: ProjectChecklistUpdateItemNote): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/checklists/itemNote`, item);
  }

  upsertProjectChecklist$(checklist: ProjectChecklist): Observable<ProjectChecklist> {
    return this.http.put<ProjectChecklist>(`${this.apiService.centralApiBaseUri}/api/checklists/upsert`, checklist);
  }

  deleteProjectChecklist$(checklistId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiService.centralApiBaseUri}/api/checklists/${checklistId}`);
  }
}
